import { IBwjAutocompleteHints } from "../entities/bwj-autocomplete";
import { BwjInput } from "../entities/bwj-input";
import { BwjValidityState } from "../entities/bwj-validity-state";
import { isNullOrWhitespace } from "../utils";
import { forEach, firstOrDefault } from "../utils/collection";


type ChangePublisher = (propertyName: string) => void;

export interface IFormModelChange<TFormModel> {
    propertyChanged: string;
    model: TFormModel;
}

export interface IFormValidityCheck<TFormModel> {
    isValid: boolean;
    model: TFormModel;
}

abstract class FormValueService<TDataModel, TInValue, TOutValue> {
    constructor(public id: string, public description: string = '') {
        this.value = this.getDefaultValue();
    }

    protected abstract getDefaultValue(): TInValue;

    public abstract initialize(data: TDataModel): void;
    public abstract applyToDataModel(data: TDataModel): void;
    public abstract getInputValue(): TInValue;
    public abstract getOutputValue(): TOutValue;

    protected value: TInValue;
}

abstract class ReadWriteFormValueService<TDataModel, TInValue, TOutValue> extends FormValueService<TDataModel, TInValue, TOutValue> {
    public setChangePublisher(fn: ChangePublisher) {
        this.publishChange = fn;
    }

    protected publishChange?: ChangePublisher;
}

/**
 * ImmutableFormValueService manages a value which is not validated, and is only set at form initialization.
 * This service does not support value transform, i.e. the data type and value fed into the service is always getOutputValue() returns
*/
export abstract class ImmutableFormValueService<TDataModel, TValue> extends FormValueService<TDataModel, TValue, TValue> {
    constructor(id: string = '', description: string = '') {
        super(id, description);
    }

    public getOutputValue(): TValue {
        return this.getInputValue();
    }
}

/**
 * MutableFormValueService manages a value which can be changed after form initialization
 * This service support value transformation, i.e. the data type and value returned by getOutputValue() may differ
 * from the value fed into the service at or after initialization
 */
export abstract class MutableFormValueService<TDataModel, TInValue, TOutValue = TInValue> extends ReadWriteFormValueService<TDataModel, TInValue, TOutValue> {
    constructor(id: string, description: string) {
        super(id, description);
        this.onUpdate = this.onUpdate.bind(this);
    }

    public abstract onUpdate(value: TInValue): void;
    public abstract setInputValue(value: TInValue): void;
}

/**
 * ValidatedFormValueService manages a mutable value which may not be assumed to be valid
 */
export abstract class ValidatedFormValueService<TDataModel, TInValue, TOutValue = TInValue> extends ReadWriteFormValueService<TDataModel, BwjInput<TInValue>, TOutValue>{
    constructor(id: string, description: string) {
        super(id, description);
        this.validate = this.validate.bind(this);
        this.onUpdate = this.onUpdate.bind(this);
    }

    public abstract setInputValue(value: BwjInput<TInValue>): void;
    public abstract onUpdate(value: TInValue): void;
    public abstract validate(value: TInValue): Promise<BwjInput<TInValue>>;

    // Intended to be invoked by formValidateAndUpdate.
    // Override this method when special validation logic is needed when the form is
    // being validated as a whole
    // This is useful in scenarios where there is an additional check to be performed before submitting
    // a request (e.g. ensure uniqueness of the value within the data store)
    public formValidate(): Promise<BwjInput<TInValue>> {
        return this.validate(this.getInputValue().value);
    }

    public formValidateAndUpdate(): Promise<void> {
        return this.formValidate().then(result => this.setInputValue(result));
    }

    public isValueUntouched(): boolean {
        return this.valueUntouched;
    }
    
    protected requiredFieldValidator(value: TInValue, errorMessage: string): BwjInput<TInValue> {
        let invalid:boolean = (typeof value === 'string') ? isNullOrWhitespace(value) : !value;

        const result = invalid?
            new BwjInput<TInValue>(value, BwjValidityState.Invalid, errorMessage) :
            new BwjInput<TInValue>(value, BwjValidityState.Valid);

        return result;
    }

    protected getInputObject(value: TInValue): BwjInput<TInValue> {
        return new BwjInput<TInValue>(value);
    }

    protected valueUntouched: boolean = true;
}

export abstract class AutocompletedFormValueService<TDataModel, TOutValue = string> extends ValidatedFormValueService<TDataModel, string, TOutValue> {
    constructor(
        id: string,
        description: string,
        public inputRequired: boolean,
        public minInputLength: number,
        private noInputMessage: string,
        private hintRetrievalErrorMessage: string,
        private invalidInputMessage: string,
        private allowHintMismatch: boolean) {
        super(id, description);
        this.validate = this.validate.bind(this);
        this.getHints = this.getHints.bind(this);
    }

    protected abstract hintProvider(topic: string): Promise<IBwjAutocompleteHints>;

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getHints(topic: string): Promise<IBwjAutocompleteHints> {
        // use existing hints if possible
        if(!!this.hints && this.hints.success &&
            this.hints.topic === topic) {
                return Promise.resolve(this.hints);
        }

        this.retrievingHints++;
        const finalmente = () => {
            this.retrievingHints--;

            // resolve any promises waiting for hints to arrive
            if(this.retrievingHints === 0) {
                forEach(this.hintWaitingQueue, (resolve) => {
                    resolve();
                });
                this.hintWaitingQueue = [];
            }
        };

        return this.hintProvider(topic)
            .then((value) => {
                this.hints = value;
                finalmente();
                return value;
            })
            .catch(() => {
                // never a rejected promise, always return a result -- but success in this case is false
                this.hints = {
                    topic: topic,
                    success: false,
                    options: []
                };
                finalmente();
                return Promise.resolve(this.hints);
            });
    }

    public validate(value: string): Promise<BwjInput<string>> {
        value = !!value ? value.trim() : value;
        const result: BwjInput<string> = { 
            id: this.id,
            description: this.description,
            value: value,
            validity: BwjValidityState.Valid };

        // validate empty string...
        if(isNullOrWhitespace(value)) {
            // if not required, valid simply if empty
            if(this.inputRequired === false) {
                return Promise.resolve(result);
            } else {  // conversely, invalid if required yet empty
                return Promise.resolve(this.requiredFieldValidator(value, this.noInputMessage));
            }
        }

        // if we are allowing hint mismatches, input is valid
        if(this.allowHintMismatch)  { return Promise.resolve(result); }

        // insufficient characters to perform hint retrieval means input is definitely invalid
        if(value.length < this.minInputLength) { 
            result.message = this.invalidInputMessage;
            result.validity = BwjValidityState.Invalid;
            return Promise.resolve(result);
         }

         return this.waitForHints().then(() => {
             //if no hints, failed to get hints, or hints not for value, get a fresh set of hints
             if(!this.hints ||
                !this.hints.success ||
                value.toLowerCase().indexOf(this.hints.topic) !== 0) {
                    return this.getHints(value.toLowerCase().substring(0, this.minInputLength)).then(() => {
                        return this.completeValidation(value);
                    });

                } else {
                    return this.completeValidation(value);
                }
         });
    }

    private waitForHints(): Promise<void> {
        if(this.retrievingHints === 0) {
            return Promise.resolve();
        } else {
            // subscribe resolve function to be fired when hints are retrieved
            const p = new Promise<void>((resolve) => {
                this.hintWaitingQueue.push(resolve);
            });
            return p;
        }
    }

    private completeValidation(v: string): BwjInput<string> {
        const result: BwjInput<string> = {
            value: v,
            validity: BwjValidityState.Valid
        };
        const hints = this.hints as IBwjAutocompleteHints;

        // failed hint retrieval
        if(!hints.success)  { 
            result.message = this.hintRetrievalErrorMessage;
            result.validity = BwjValidityState.ValidationError;
            return result;
        }

        // input must match a hint
        const hint = firstOrDefault(hints.options, h => h.value === v);
        if (!hint)  { 
            result.message = this.invalidInputMessage;
            result.validity = BwjValidityState.Invalid;
            return result;
        }

        result.additionalInfo = hint;
        return result; // success
    }

    private hints: IBwjAutocompleteHints | null = null;
    private retrievingHints: number = 10000;
    private hintWaitingQueue: Array<() => void> = [];
}